import type { ActivityDatePreference } from './activity-date-preference.types';

import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { ConfirmDialog } from 'src/components/custom-dialog';
import { Iconify } from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';

import { ActivityDatePreferenceActionsMenu } from './activity-date-preference-actions-menu';
import { getInclusiveDurationDays } from './derive-activity-date-preferences';

interface ActivityDatePreferencesProps {
  preferences: ActivityDatePreference[];
  isSaving?: boolean;
  onAddPreference?: () => void;
  onEditPreference?: (preference: ActivityDatePreference) => void;
  onDeletePreference?: (preferenceId: string | number) => void | Promise<void>;
}

const HEADER_CELL_SX = {
  py: 1,
  px: 2,
  color: 'text.secondary',
  fontWeight: 600,
  fontSize: 14,
  borderBottom: 1,
  borderColor: 'divider',
} as const;

const BODY_CELL_SX = {
  py: 1,
  px: 2,
  fontSize: 14,
  borderBottom: 'none',
} as const;

export const ActivityDatePreferences = memo(
  ({
    preferences,
    isSaving = false,
    onAddPreference,
    onEditPreference,
    onDeletePreference,
  }: ActivityDatePreferencesProps) => {
    const { t } = useTranslation(['activities', 'common']);
    const deleteConfirm = useBoolean();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [activePreference, setActivePreference] = useState<ActivityDatePreference | null>(null);

    const handleOpenMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>, preference: ActivityDatePreference) => {
        setMenuAnchorEl(event.currentTarget);
        setActivePreference(preference);
      },
      []
    );

    const handleCloseMenu = useCallback(() => {
      setMenuAnchorEl(null);
    }, []);

    const handleEdit = useCallback(() => {
      if (activePreference) {
        onEditPreference?.(activePreference);
      }
    }, [activePreference, onEditPreference]);

    const handleDeleteRequest = useCallback(() => {
      deleteConfirm.onTrue();
    }, [deleteConfirm]);

    const handleConfirmDelete = useCallback(async () => {
      if (activePreference) {
        await onDeletePreference?.(activePreference.id);
      }
      deleteConfirm.onFalse();
      setActivePreference(null);
    }, [activePreference, deleteConfirm, onDeletePreference]);

    const handleCancelDelete = useCallback(() => {
      deleteConfirm.onFalse();
      setActivePreference(null);
    }, [deleteConfirm]);

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 2 }}
          >
            <Typography variant="h6">{t('activityModal.dates.preferences.title')}</Typography>
            <Button
              variant="text"
              color="inherit"
              startIcon={<Iconify icon="mingcute:add-line" width={20} />}
              onClick={onAddPreference}
              disabled={isSaving}
              sx={{ fontWeight: 700 }}
            >
              {t('activityModal.dates.preferences.addPreference')}
            </Button>
          </Stack>

          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('activityModal.dates.preferences.columns.type')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('activityModal.dates.preferences.columns.start')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('activityModal.dates.preferences.columns.end')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('activityModal.dates.preferences.columns.duration')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 40, px: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {preferences.map((preference) => (
                <TableRow key={preference.id} sx={{ height: 40 }}>
                  <TableCell sx={BODY_CELL_SX}>
                    {t(`activityModal.dates.preferences.types.${preference.type}`)}
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>{preference.start.format('DD MMM YY')}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>{preference.end.format('DD MMM YY')}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    {getInclusiveDurationDays(preference.start, preference.end)}
                  </TableCell>
                  <TableCell sx={{ ...BODY_CELL_SX, width: 40, px: 0, textAlign: 'right' }}>
                    {!preference.isSystem ? (
                      <IconButton
                        size="small"
                        aria-label={t('activityModal.dates.preferences.rowActionsAriaLabel')}
                        onClick={(event) => handleOpenMenu(event, preference)}
                        disabled={isSaving}
                        sx={{ width: 36, height: 36 }}
                      >
                        <Iconify icon="eva:more-vertical-fill" width={20} />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1 }}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<Iconify icon="mingcute:add-line" width={18} />}
              onClick={onAddPreference}
              disabled={isSaving}
              sx={{ color: 'text.secondary', fontWeight: 400 }}
            >
              {t('activityModal.dates.preferences.addPreference')}
            </Button>
          </Box>
        </Box>

        <ActivityDatePreferenceActionsMenu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />

        <ConfirmDialog
          open={deleteConfirm.value}
          onClose={handleCancelDelete}
          title={t('activityModal.dates.preferences.deleteDialog.title')}
          content={t('activityModal.dates.preferences.deleteDialog.content')}
          action={
            <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={isSaving}>
              {t('common:actions.delete')}
            </Button>
          }
        />
      </>
    );
  }
);

ActivityDatePreferences.displayName = 'ActivityDatePreferences';
